// Utility for validating file types for attachment

const allowedImageExtensions = [
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'
];
const allowedWordExtensions = ['.doc', '.docx'];
const allowedExcelExtensions = ['.xls', '.xlsx'];
const allowedPowerpointExtensions = ['.ppt', '.pptx'];
const allowedPdfExtensions = ['.pdf'];

function getExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot !== -1 ? filename.slice(dot).toLowerCase() : '';
}

function isAllowedByExtension(filename: string): boolean {
  const ext = getExtension(filename);
  return (
    allowedImageExtensions.includes(ext) ||
    allowedWordExtensions.includes(ext) ||
    allowedExcelExtensions.includes(ext) ||
    allowedPdfExtensions.includes(ext) ||
    allowedPowerpointExtensions.includes(ext)
  );
}

/**
 * Determine if a file is text based on its MIME type
 */
export function isTextFile(file: File): boolean {
  const mimeType = file.type.toLowerCase();
  
  // Common text MIME types
  if (mimeType.startsWith('text/')) {
    return true;
  }
  
  // Additional text-based MIME types
  const textMimeTypes = [
    'application/json',
    'application/xml',
    'application/csv',
    'application/javascript',
    'application/typescript',
    'application/yaml',
    'application/x-yaml',
    'application/sql',
    'application/x-sql'
  ];
  
  return textMimeTypes.includes(mimeType);
}

function isProbablyText(buffer: Uint8Array): boolean {
  // Allow printable ASCII, tab, CR, LF, and a few common UTF-8 bytes
  for (let i = 0; i < buffer.length; i++) {
    const byte = buffer[i];
    if (
      (byte >= 32 && byte <= 126) || // printable ASCII
      byte === 9 || // tab
      byte === 10 || // LF
      byte === 13 // CR
    ) {
      continue;
    }
    // Allow some UTF-8 (not strict, but avoids most binary)
    if (byte >= 128 && byte <= 255) continue;
    // Otherwise, likely binary
    return false;
  }
  return true;
}

export async function validateFileType(file: File): Promise<{ valid: boolean; reason?: string }> {
  // First check if it's allowed by extension (images, Office docs, PDFs)
  if (isAllowedByExtension(file.name)) {
    return { valid: true };
  }
  
  // Check if it's a text file based on MIME type (this will catch CSV files)
  if (isTextFile(file)) {
    return { valid: true };
  }
  
  // Fallback: check if it's probably text by examining content
  // This handles cases where MIME type might not be set correctly
  const chunk = file.slice(0, 512); // Read first 512 bytes
  const buffer = new Uint8Array(await chunk.arrayBuffer());
  if (isProbablyText(buffer)) {
    return { valid: true };
  }
  
  return {
    valid: false,
    reason: 'Only plain text, images, PDF, MS PowerPoint, MS Word, or MS Excel files are allowed.'
  };
} 